import { useMemo } from 'react';
import { AutocompleteOption, useAutocomplete } from '@/common/presentation/hooks/useAutocomplete';

import { useGetSalesAgentsAutocompleteWithTakeQuery } from '@/common/infrastructure/gql/generated/schema';

type SalesAgent = NonNullable<
  NonNullable<NonNullable<ReturnType<typeof useGetSalesAgentsAutocompleteWithTakeQuery>['data']>['salesAgents']>['items']
>[number];

export const useListSalesAgentsAutocomplete = (
  searchTerm: string = '',
  debounceMs: number = 1000,
  onSalesAgentsSelect?: (salesAgents: AutocompleteOption) => void,
  take: number = 10
) => {
  const { data, loading, error, refetch } = useGetSalesAgentsAutocompleteWithTakeQuery({
    variables: { skip: 0, search: searchTerm, take },
    fetchPolicy: 'cache-and-network'
  });

  const autocompleteResult = useAutocomplete<SalesAgent>({
    searchTerm,
    debounceMs,
    onSelect: onSalesAgentsSelect,
    query: () => ({
      data: { items: data?.salesAgents?.items },
      loading,
      error,
      refetch
    }),
    transform: (salesAgents) => ({
      value: salesAgents.id ?? -1,
      label: salesAgents.name ?? 'Unknown Sales Agent'
    })
  });

  // Find default sales agent option - query all sales agents to find default
  const { data: defaultData } = useGetSalesAgentsAutocompleteWithTakeQuery({
    variables: { skip: 0, search: '', take },
    fetchPolicy: 'cache-first'
  });

  const defaultSalesAgentOption = useMemo<AutocompleteOption | undefined>(() => {
    const defaultAgent = defaultData?.salesAgents?.items?.find((agent) => {
      const withDefault = agent as { default?: boolean | null };
      return Boolean(withDefault.default) && typeof agent?.id === 'number';
    });
    if (!defaultAgent) return undefined;
    return {
      value: defaultAgent.id ?? -1,
      label: defaultAgent.name ?? 'Unknown Sales Agent'
    };
  }, [defaultData]);

  return {
    ...autocompleteResult,
    defaultSalesAgentOption
  };
};
